import { CircularProgress, Stack, Typography } from "@mui/material";
import { CustomButton } from "../../../../ui/CustomButton";
import { ReactNode, useEffect, useState } from "react";
import { UploadFromDesktopMethod } from "./UploadFromDesktopMethod";
import { CRMUploadMethod, UploadMethod } from "./CRMUploadMethod";
import {
  Account,
  CampaignSavedData,
  CIDAddedType,
  CRMData,
} from "../../../../../types/apiTypes";
import {
  useCreateCampaignMutation,
  useCreateImportFromCRMMutation,
  useEnterCIDMutation,
  useEnterCompanyManuallyMutation,
  useUploadCampaignFilesMutation,
} from "../../../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../../../ui/CustomToast";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { EnterManually } from "./EnterManually";
import { AppRoutes, UploadMethodsOption } from "../../../../../types/enums";
import { useNavigate } from "react-router-dom";
import { NAME_REGEX, URL_REGEX } from "../../../../../utils/regex";
import { ReuseModal } from "../../../../ui";
import { clearCreateFlowLocalStorage } from "../../../../../utils/clearCreateFlowLocalStorage";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { setImportDataStatus } from "../../../../../features/companySlice";
import { DataUploadHeaderBlock } from "./DataUploadHeaderBlock";
import { UploadCidsMethod } from "./UploadCidsMethod";

type SavedData = CRMData | null;

export const DataUploadMethods = ({
  resultData,
  backHandler,
}: {
  resultData: CampaignSavedData;
  backHandler: () => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const importDataStatus = useAppSelector(
    (state) => state.company.importDataStatus,
  );

  const [currentCampaignId, setCurrentCampaignId] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentCrm, setCurrentCrm] = useState<UploadMethod>("");
  const [currentMethod, setCurrentMethod] = useState<UploadMethodsOption>(
    UploadMethodsOption.crm,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [companies, setCompanies] = useState<Account[]>([]);
  const [dataSourceList, setDataSourceList] = useState<CRMData | null>(null);
  const [cids, setCids] = useState<CIDAddedType[]>([]);

  const [createCampaign] = useCreateCampaignMutation();
  const [enterCompanyManually] = useEnterCompanyManuallyMutation();
  const [uploadFiles] = useUploadCampaignFilesMutation();
  const [importFromCRM] = useCreateImportFromCRMMutation();
  const [enterCID] = useEnterCIDMutation();

  useEffect(() => {
    return () => {
      dispatch(setImportDataStatus(null));
    };
  }, []);

  useEffect(() => {
    if (
      importDataStatus &&
      currentCampaignId === importDataStatus.campaign_id
    ) {
      if (importDataStatus.status === "failed") {
        setIsLoading(false);
        dispatch(setImportDataStatus(null));
        setDataSourceList(null);
        setCurrentCrm("");
        toast.error(
          <CustomToast
            type="error"
            text={importDataStatus.message || "The company data upload failed."}
            title="Upload Failed"
          />,
        );
      }

      if (importDataStatus.status === "completed") {
        navigate(`${AppRoutes.CAMPAINGS}/${importDataStatus.campaign_id}`);
        clearCreateFlowLocalStorage();
        toast.success(
          <CustomToast
            type={"success"}
            text={"Check product information and accounts analysis results."}
            title={"New Product Was Added"}
          />,
        );
      }

      if (importDataStatus.status === "in_progress") {
        setIsLoading(true);
      }
    }
  }, [importDataStatus]);

  useEffect(() => {
    if (dataSourceList) {
      localStorage.setItem("crm", JSON.stringify(dataSourceList));
      setCurrentCrm(dataSourceList?.crm || "");
    }
  }, [dataSourceList]);

  useEffect(() => {
    const data = localStorage.getItem("crm");
    if (data) {
      const savedDataSourceList = JSON.parse(data) as SavedData;
      if (savedDataSourceList) {
        setCurrentCrm(savedDataSourceList.crm);
        setDataSourceList(savedDataSourceList);
      }
    }
  }, []);

  const payload = { ...resultData };
  delete payload.target_account_sizes;
  delete payload.error;

  const getCampaignId = async () => {
    return await createCampaign({
      ...payload,
      target_revenue: payload.target_revenue?.map(({ value }) => value) || [],
      target_industries: (payload.target_industries || []).map(
        ({ name, sub_industries, is_all_sub_industries }) => ({
          name,
          sub_industries: sub_industries.map(({ name }) => name),
          is_all_sub_industries: !!is_all_sub_industries,
        }),
      ),
      target_audiences: payload.target_titles || [],
      target_account_size: (resultData.target_account_sizes || []).map(
        ({ min_max }) => min_max,
      ),
      target_regions: (payload.target_regions || []).map(
        ({ name, countries, is_all_countries }) => ({
          name,
          countries: countries.map(({ name }) => name),
          is_all_countries: !!is_all_countries,
        }),
      ),
    })
      .unwrap()
      .then((campaign) => {
        setCurrentCampaignId(campaign.campaign_id);
        return campaign.campaign_id;
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "The company data upload failed."
            }
            title="Upload Failed"
          />,
        );
        setIsLoading(false);
      });
  };

  const isFromDesktop =
    currentMethod === UploadMethodsOption.desktop && files.length > 0;
  const isFromCRM =
    currentMethod === UploadMethodsOption.crm && !!dataSourceList;
  const isFromManually =
    currentMethod === UploadMethodsOption.manually &&
    companies.length > 0 &&
    companies.every(
      ({ name, website_url }) =>
        name.trim() &&
        NAME_REGEX.test(name) &&
        (!website_url || URL_REGEX.test(website_url)),
    );

  const isCID = currentMethod === UploadMethodsOption.cid && cids.length > 0;

  const isUploadLater =
    !isFromDesktop && !isFromCRM && !isFromManually && !isCID;

  const navigationMethods = [
    { label: UploadMethodsOption.crm },
    { label: UploadMethodsOption.desktop },
    {
      label: UploadMethodsOption.manually,
    },
    {
      label: UploadMethodsOption.cid,
    },
  ];

  const methods: Record<string, ReactNode> = {
    [UploadMethodsOption.desktop]: (
      <UploadFromDesktopMethod
        isLoading={isLoading}
        setFiles={setFiles}
        maxFiles={5}
      />
    ),
    [UploadMethodsOption.crm]: (
      <CRMUploadMethod
        currentCrm={currentCrm}
        setDataSource={setDataSourceList}
        isDisabled={false}
      />
    ),
    [UploadMethodsOption.manually]: (
      <EnterManually isDisabled={isLoading} setCompanies={setCompanies} />
    ),
    [UploadMethodsOption.cid]: (
      <UploadCidsMethod cids={cids} setCids={setCids} isLoading={isLoading} />
    ),
  };

  const submitFromDesktopHandler = async (campaign_id: string) => {
    await uploadFiles({
      campaign_id,
      files,
      import_contacts: true,
    })
      .unwrap()
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Upload Failed"}
          />,
        );
        setIsLoading(false);
      });
  };

  const submitManuallyHandler = async (campaign_id: string) => {
    await enterCompanyManually({
      campaign_id,
      companies,
    })
      .unwrap()
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again."
            }
            title={"Something Went Wrong"}
          />,
        );
        setIsLoading(false);
      });
  };

  const submitCRMHandler = async (
    campaign_id: string,
    import_contacts: boolean,
  ) => {
    let list_id: string | undefined;
    if (dataSourceList?.list_id && dataSourceList?.list_id.length > 0) {
      list_id = dataSourceList.list_id;
    }

    await importFromCRM({
      campaign_id,
      import_contacts,
      crm: dataSourceList?.crm!,
      list_id,
      first_import: true,
    })
      .unwrap()
      .catch((err) => {
        setIsLoading(false);
        setDataSourceList({ list_id: undefined, crm: "" });
        setCurrentCrm("");

        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again."
            }
            title="Create Campaign Failed"
          />,
        );
      });
  };

  const uploadLaterHandler = async () => {
    setIsLoading(true);
    setIsOpen(false);

    await createCampaign({
      ...payload,
      target_revenue: payload.target_revenue?.map(({ value }) => value) || [],
      target_audiences: payload.target_titles || [],
      create_empty: true,
      target_account_size: (resultData.target_account_sizes || []).map(
        (size) => size.min_max,
      ),
      target_industries: (payload.target_industries || []).map(
        ({ name, sub_industries, is_all_sub_industries }) => ({
          name,
          sub_industries: sub_industries.map(({ name }) => name),
          is_all_sub_industries: !!is_all_sub_industries,
        }),
      ),
      target_regions: (payload.target_regions || []).map(
        ({ name, countries, is_all_countries }) => ({
          name,
          countries: countries.map(({ name }) => name),
          is_all_countries: !!is_all_countries,
        }),
      ),
    })
      .unwrap()
      .then((campaign) => {
        navigate(`${AppRoutes.CAMPAINGS}/${campaign.campaign_id}`);
        clearCreateFlowLocalStorage();
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "The company data upload failed."
            }
            title="Upload Failed"
          />,
        );
        setIsLoading(false);
      });
  };

  const submitCIDHandler = async (campaign_id: string) => {
    await enterCID({
      campaign_id,
      cids: cids.map(({ cid, sub_cids }) => ({
        cid,
        sub_cids: sub_cids.map(({ name }) => name),
      })),
    })
      .unwrap()
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again."
            }
            title={"Something Went Wrong"}
          />,
        );
        setIsLoading(false);
      });
  };

  const submitHandler = async () => {
    if (isUploadLater) {
      if (companies.length > 0) {
        setIsOpen(true);
        return;
      }
      await uploadLaterHandler();
      return;
    }

    setIsLoading(true);
    let id: string = "";

    if (getCampaignId) id = (await getCampaignId()) || "";
    if (!id) return setIsLoading(false);

    if (isFromManually) await submitManuallyHandler(id);
    if (isFromCRM) await submitCRMHandler(id, true);
    if (isFromDesktop) await submitFromDesktopHandler(id);
    if (isCID) await submitCIDHandler(id);
  };

  return (
    <Stack gap="16px">
      <Stack gap="16px">
        <Stack gap="8px">
          <Typography
            component="span"
            variant="subtitle1"
            color="custom.textDarkBlue"
            fontWeight={500}
          >
            Select the data upload method
          </Typography>
        </Stack>

        <Stack flexDirection="row" gap="8px" flexWrap="nowrap">
          {navigationMethods.map(({ label }) => (
            <CustomButton
              key={label}
              onClick={() => setCurrentMethod(label)}
              variant="text"
              type="button"
              sx={{
                gap: 0,
                textWrap: "nowrap",
                color: `${label === currentMethod ? "#1C3C6C" : "#4C545B"}`,
                cursor: `${label === currentMethod ? "auto" : "pointer"}`,
                backgroundColor: `${
                  label === currentMethod ? "#F1F1F1" : "#F1F1F14D"
                }`,
                "&:hover": {
                  color: "#1C3C6C",
                  backgroundColor: "#F1F1F1",
                },
              }}
              fullWidth
              disabled={isLoading}
            >
              {label}
            </CustomButton>
          ))}
        </Stack>
        <DataUploadHeaderBlock method={currentMethod} />
      </Stack>
      {methods[currentMethod]}
      <Stack direction="row" gap="8px">
        <CustomButton
          onClick={backHandler}
          variant="contained"
          textColor="#4C545B"
          fullWidth
          type="button"
          customColor="#F1F1F1"
          disabled={isLoading}
        >
          Back
        </CustomButton>
        <CustomButton
          onClick={submitHandler}
          variant="contained"
          fullWidth
          type="button"
          customColor="#1C3C6C"
          disabled={isLoading}
        >
          {isUploadLater
            ? "Save and Upload Later"
            : isLoading && !importDataStatus
              ? "Creating Campaign"
              : importDataStatus?.campaign_id === currentCampaignId
                ? `Processing Data: ${importDataStatus.progress}%`
                : "Save and Analyze"}
          {isLoading && <CircularProgress size={20} color="inherit" />}
        </CustomButton>
      </Stack>
      <ReuseModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Are You Sure You Want to Upload Data Later?"
        maxWidth="430px"
        maxTitleWidth="300px"
        submitBtnText="Upload Later"
        submitBtnColor="#C0392B"
        onSubmit={uploadLaterHandler}
        text="All files added on this step will be removed."
      />
    </Stack>
  );
};
